import { useState, useEffect, useCallback } from "react";
import { useLoaderData, useParams } from "react-router-dom";

import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import { useProviderSearchStore } from "../store/providerSearchStore";

export function useAddresses() {
  const caseRepository = useService("repositories/case-repository");
  const params = useParams();
  const { _case } = useLoaderData();
  const caseId = params?.id ?? _case?.id ?? _case?.pk;
  const { initAddress, setInitAddress, setLoading } = useProviderSearchStore();
  const [addressData, setAddressData] = useState([]);
  const [addressSearch, setAddressSearch] = useState("");
  const [addressShowMore, setAddressShowMore] = useState(false);
  const [addressLimit, setAddressLimit] = useState(25);
  const [addressOffset, setAddressOffset] = useState(0);

  const fetchAddresses = useCallback(
    async (append = false, isNewSearch = true) => {
      setLoading(true);
      try {
        let limitVal = addressLimit;
        let offsetVal = addressOffset;

        if (isNewSearch) {
          limitVal = 25;
          offsetVal = 0;
          setAddressLimit(25);
          setAddressOffset(0);
        }

        const params = { limit: limitVal, offset: offsetVal };

        const response = await caseRepository.getAddresses(caseId, params);
        const mapped =
          response?.map((item, index) => ({
            id: index,
            name: `${item.label} ${!item?.showSearch ? "(" + item.address + ")" : ""}`,
            label: item.label ?? "Custom Address",
            additionalDetails: {
              id: item?.id,
              latitude: item?.latitude,
              longitude: item?.longitude,
              showSearch: item?.showSearch,
            },
          })) || [];

        const nextPageURL = await caseRepository.getNextPage();
        if (nextPageURL) {
          setAddressShowMore(true);
          const urlObj = new URL(nextPageURL);
          setAddressLimit(parseInt(urlObj.searchParams.get("limit")) || limitVal);
          setAddressOffset(parseInt(urlObj.searchParams.get("offset")) || offsetVal);
        } else {
          setAddressShowMore(false);
        }

        const newData = append ? [...addressData, ...mapped] : mapped;
        if (!initAddress && newData.length > 0) {
          setInitAddress(newData[0]);
        }
        setAddressData(newData);
      } catch (error) {
        console.error(error);
        return ApiRequestErrorHandler(error);
      } finally {
        setLoading(false);
      }
    },
    [addressSearch, addressLimit, addressOffset, caseRepository, addressData]
  );

  useEffect(() => {
    if (caseId) {
      fetchAddresses(false, true);
    }
  }, [addressSearch]);

  const handleShowMoreAddresses = () => fetchAddresses(true, false);

  return {
    initAddress,
    fetchAddresses,
    addressData,
    addressSearch,
    setAddressSearch,
    addressShowMore,
    handleShowMoreAddresses,
  };
}
